
	import { defineComponent, PropType, Ref, ref } from 'vue';

	import { WorkflowService } from '@/Services/workflow-service';
	import { ValidationHelper } from '@/Services/Helper/validation-helper';
	import LoadingSpinner from '@/ChildrenComponents/LoadingSpinner.vue';

	import { IWorkflowStatus } from '@/Models/Workflow/IWorkflowStatus';
	import { IWorkflowSubmit } from '@/Models/IWorkflowSubmit';
	import { IValidationResult } from '@/Models/IValidationResult';

	export default defineComponent({
		name: 'workflow-submission',
		components: { LoadingSpinner },
		emits: {
			[`partial-save`]: (payload: boolean) => payload,
			[`submit-do-more`]: (payload: number | null) => payload,
			[`submit-step`]: (payload: number | null) => payload,
			[`failed-validation`]: (payload: IValidationResult) => payload,
			[`request-parent-save`]: () => true,
		},
		setup(): { workflowForm: Ref } {
			const workflowForm = ref(null);
			return {
				workflowForm
			};
		},
		data() {
			return {
				nextWorkflowStatusSelectItems: [] as IWorkflowStatus[],
				statusFieldRules: ValidationHelper.requiredRules,
				noteFieldRules: ValidationHelper.requiredRules,
				selectedNextStatusId: null as number | null,
				notes: "" as string,
				isLoading: false as boolean,
				saveButtonsDisabled: false as boolean,
			}
		},
		props: {
			suppliedWorkflowInstanceId: {
				type: Number as PropType<number>,
				required: true
			},
			showPartialSaveOptions: {
				type: Boolean as PropType<boolean>,
				required: true
			},
            defaultNextWorkflowStatusId: {
                type: Number,
                required: false
            },
            isParentLoading: {
                type: Boolean,
                required: false
            },
            title: {
                type: String,
                required: false
            },
            variant: {
                type: String,
                default: "elevated"
            },
            showSubmitAndDoMore: {
                type: Boolean,
                required: false
            },
            submitAndDoMoreText: {
                type: String,
                default: "Submit And More"
            },
			beforeSubmitFunction: {
				type: Function,
				required: false
			}
		},
		watch: {
			selectedNextStatusId() {
				const selectedStatus = this.nextWorkflowStatusSelectItems.find(x => x.workflowStatusId == this.selectedNextStatusId);
				this.noteFieldRules = selectedStatus != undefined && selectedStatus.isNoteRequired ? ValidationHelper.requiredRules : [];
			},
			suppliedWorkflowInstanceId: {
				handler(newValue: number): void{
					if (newValue > 1) {
						this.fetchApprovedStatusIds();
						return;
					}
					else {
						return;
					}
				},
				immediate: true
			},
		},
		methods: {  
			fetchApprovedStatusIds(): void {
				this.isLoading = true;
				WorkflowService.listNextWorkflowStatuses(this.suppliedWorkflowInstanceId)
					.then((response) => {
						this.nextWorkflowStatusSelectItems = response;
						if (this.defaultNextWorkflowStatusId && this.nextWorkflowStatusSelectItems.some(x => x.workflowStatusId == this.defaultNextWorkflowStatusId)) {
							this.selectedNextStatusId = this.defaultNextWorkflowStatusId;
						}
					})
					.finally(() => this.isLoading = false);
			},
			savePartial(): void {
				this.saveButtonsDisabled = true;
				this.$emit('partial-save', true);
				this.saveButtonsDisabled = false;
			},
			async submitWorkflowStep(doMore: boolean): Promise<void> {
				// since this gets processed as a workflow divorced from the dto object itself, we may need to save
				// the object if we're going to use it for server side validation. we emit a save note here to
				// allow a parent to partially save if that's appropriate for the specific workflow
				// this is the case for the drug tests (as an example)
				
				//emit event cannot be async/await'd
				this.$emit('request-parent-save');

				//update to pass the function as a prop to make it truly async/awaitable
				if (this.beforeSubmitFunction) await this.beforeSubmitFunction();

				const { valid } = await this.workflowForm.validate();
				if (valid) {
					this.saveButtonsDisabled = true;
					const submitDto = {
						workflowInstanceId: this.suppliedWorkflowInstanceId,
						note: this.notes,
						newWorkflowStatusId: this.selectedNextStatusId,
						assignedUserId: null
					} as IWorkflowSubmit;
					WorkflowService.submit(submitDto).then(res => {
						if (!res.isValid) {
							this.$emit('failed-validation', res);
							this.saveButtonsDisabled = false;
							return;
						}
	
						if (doMore) {
                            this.$emit('submit-do-more', this.selectedNextStatusId);
                        } else {
                            this.$emit('submit-step', this.selectedNextStatusId);
                        }
					}).finally(() => {
						this.saveButtonsDisabled = false;
					})
				} else {
					this.saveButtonsDisabled = false;
				}
			},
		}
	}); 
